// src/components/StallCards.tsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWhatsapp, 
  faFacebook, 
  faXTwitter, 
  faTiktok, 
  faYoutube, 
  faInstagram 
} from '@fortawesome/free-brands-svg-icons';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt, 
  faTag, 
  faStore, 
  faIdCardClip, 
  faArrowRight,
  faStar,
  faImages,
  faList
} from '@fortawesome/free-solid-svg-icons';
import { StallDisplayData } from '../Home';

// Import styled components
import {
  CardContainer,
  PremiumCardContainer,
  PremiumLabel,
  MediaContainer,
  PremiumMediaContainer,
  CardHeader,
  BusinessTitle,
  CombinedMeta, 
  BusinessMeta,
  MetaItem,
  MetaIcon,
  MetaText,
  CardBody,
  BadgeContainer,
  Badge,
  CardFooter,
  ActionBar,
  FullWidthContactButton, 
  ViewDetailsButton,
  SocialIconsRow,
  SocialIcon
} from '../styles/StallCards.styles';
import { MediaCarousel } from '../MediaCarousel';

// --- Interfaces ---
export interface StallCardProps {
  stall: StallDisplayData;
  handleCardClick: (id: string) => void;
  handleImageClick: (imageUrl: string) => void;
  shuffleCount?: number;
  shouldFlip?: boolean;
}

// --- SAFE MEDIA EXTRACTOR (Critical Fix) ---
const getMediaDetails = (stall: StallDisplayData): any[] => {
  // NEW format: mediaDetails directly on stall (from optimized Home.tsx)
  if (stall.mediaDetails && Array.isArray(stall.mediaDetails) && stall.mediaDetails.length > 0) {
    return stall.mediaDetails;
  }

  // OLD format fallback: listings[].mediaDetails
  if (stall.listings && Array.isArray(stall.listings)) {
    return stall.listings
      .flatMap((listing: any) => listing.mediaDetails || [])
      .filter((m: any) => m && typeof m === 'object' && m.url);
  }

  // Final fallback
  return [];
};

// --- Helper Functions ---
const formatPhoneNumber = (phone: string): string => {
  return phone.replace(/\s/g, '');
};

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// --- Regular Stall Card ---
export const StallCard: React.FC<StallCardProps> = ({ stall, handleCardClick, handleImageClick }) => {
  const handleClick = () => handleCardClick(stall.id);

  // Use safe extractor
  const mediaDetails = getMediaDetails(stall);

  return (
    <CardContainer>
      <MediaContainer onClick={handleClick}>
        <MediaCarousel
          mediaDetails={mediaDetails}
          title={stall.title}
          handleImageClick={handleImageClick}
          isPremium={false}
        />
      </MediaContainer>

      <CardHeader>
        <BusinessTitle>{truncateText(stall.title, 40)}</BusinessTitle>
      </CardHeader>

      <CombinedMeta>
        <BusinessMeta>
          <MetaItem>
            <MetaIcon icon={faStore} />
            <MetaText>{stall.StallNumber || 'N/A'}</MetaText>
          </MetaItem>
          <MetaItem>
            <MetaIcon icon={faMapMarkerAlt} />
            <MetaText>{truncateText(stall.location || 'Location', 20)}</MetaText>
          </MetaItem>
        </BusinessMeta>
        <BadgeContainer>
          <Badge type="category">{truncateText(stall.category || 'General', 15)}</Badge>
        </BadgeContainer>
      </CombinedMeta>

      <CardFooter>
        <ActionBar>
          {stall.businessPhone && (
            <FullWidthContactButton buttonType="phone" href={`tel:${formatPhoneNumber(stall.businessPhone)}`} className="premium">
              <FontAwesomeIcon icon={faPhone} />
            </FullWidthContactButton>
          )}
          {stall.whatsappPhone && (
            <FullWidthContactButton buttonType="whatsapp" href={`https://wa.me/${formatPhoneNumber(stall.whatsappPhone)}`} target="_blank" className="premium">
              <FontAwesomeIcon icon={faWhatsapp} />
            </FullWidthContactButton>
          )}
        </ActionBar>
        <ViewDetailsButton onClick={handleClick} className="premium-view-details-button">
          View this Business <FontAwesomeIcon icon={faArrowRight} />
        </ViewDetailsButton>
      </CardFooter>
    </CardContainer>
  );
};

// --- Premium Stall Card ---
export const PremiumStallCard: React.FC<StallCardProps> = ({ 
  stall, 
  handleCardClick, 
  handleImageClick, 
  shuffleCount, 
  shouldFlip = false 
}) => {
  const [isFlipping, setIsFlipping] = useState(false);

  useEffect(() => {
    if (shuffleCount !== undefined && shouldFlip) {
      setIsFlipping(true);
      const timer = setTimeout(() => setIsFlipping(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [shuffleCount, shouldFlip]);

  const handleClick = () => handleCardClick(stall.id);

  // Use safe extractor
  const mediaDetails = getMediaDetails(stall);

  return (
    <PremiumCardContainer flip={isFlipping} className="premium-card">
      <PremiumLabel>
        <FontAwesomeIcon icon={faStar} style={{ marginRight: '4px' }} />
        Premium
      </PremiumLabel>
      
      <PremiumMediaContainer onClick={handleClick}>
        <MediaCarousel
          mediaDetails={mediaDetails}
          title={stall.title}
          handleImageClick={handleImageClick}
          isPremium={true}
        />
      </PremiumMediaContainer>

      <BusinessTitle className="premium-card-title">
        {stall.title}
      </BusinessTitle>
      
      <BusinessMeta className="premium-business-meta">
        {stall.StallNumber && (
          <MetaItem>
            <MetaIcon icon={faStore} />
            <MetaText>{stall.StallNumber}</MetaText>
          </MetaItem>
        )}
        {stall.location && (
          <MetaItem>
            <MetaIcon icon={faMapMarkerAlt} />
            <MetaText>{stall.location}</MetaText>
          </MetaItem>
        )}
      </BusinessMeta>

      {stall.category && (
        <BadgeContainer>
          <Badge type="category">{stall.category}</Badge>
        </BadgeContainer>
      )}

      <CardFooter style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column' }}>
        <ActionBar className="premium-action-bar">
          {stall.businessPhone && (
            <FullWidthContactButton buttonType="phone" href={`tel:${formatPhoneNumber(stall.businessPhone)}`} className="premium">
              <FontAwesomeIcon icon={faPhone} />
            </FullWidthContactButton>
          )}
          {stall.email && (
            <FullWidthContactButton buttonType="email" href={`mailto:${stall.email}`} className="premium">
              <FontAwesomeIcon icon={faEnvelope} />
            </FullWidthContactButton>
          )}
          {stall.whatsappPhone && (
            <FullWidthContactButton buttonType="whatsapp" href={`https://wa.me/${formatPhoneNumber(stall.whatsappPhone)}`} target="_blank" className="premium">
              <FontAwesomeIcon icon={faWhatsapp} />
            </FullWidthContactButton>
          )}
          <ViewDetailsButton onClick={handleClick} className="premium-view-details-button">
            View this Business <FontAwesomeIcon icon={faArrowRight} />
          </ViewDetailsButton>
        </ActionBar>
      </CardFooter>
    </PremiumCardContainer>
  );
};